import { z } from 'zod';
import { latLngSchema, uuidSchema } from './common.js';
/** Partita IVA: 11 digits. */
const vatNumberSchema = z
    .string()
    .trim()
    .regex(/^(IT)?\d{11}$/i, { message: 'validation.vatNumber' });
/** Codice Fiscale: 16 alphanumeric for individuals, 11 digits for companies. */
const fiscalCodeSchema = z
    .string()
    .trim()
    .regex(/^([A-Za-z0-9]{16}|\d{11})$/, { message: 'validation.fiscalCode' });
/** Provincia: two letters, e.g. MI, RM, BO. */
const provinciaSchema = z
    .string()
    .trim()
    .length(2)
    .regex(/^[A-Za-z]{2}$/, { message: 'validation.provincia' })
    .transform((v) => v.toUpperCase());
/** CAP: five digits. */
const capSchema = z
    .string()
    .trim()
    .regex(/^\d{5}$/, { message: 'validation.cap' });
const timeOfDaySchema = z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/, { message: 'validation.time' });
export const createCustomerSchema = z
    .object({
    code: z.string().trim().min(1).max(24),
    businessName: z.string().trim().min(1).max(200),
    vatNumber: vatNumberSchema.nullish(),
    fiscalCode: fiscalCodeSchema.nullish(),
    address: z.string().trim().max(300).nullish(),
    provincia: provinciaSchema.nullish(),
    comune: z.string().trim().max(120).nullish(),
    cap: capSchema.nullish(),
    phone: z
        .string()
        .trim()
        .regex(/^[+0-9 ()./-]{6,24}$/, { message: 'validation.phone' })
        .nullish(),
    email: z.string().trim().email({ message: 'validation.email' }).max(160).nullish(),
    contactPerson: z.string().trim().max(120).nullish(),
    notes: z.string().trim().max(2000).nullish(),
    /** Set by dropping a pin or by geocoding the address. */
    location: latLngSchema.nullish(),
    geofenceRadiusMeters: z.coerce.number().int().min(20).max(2000).optional(),
    deliveryWindowStart: timeOfDaySchema.nullish(),
    deliveryWindowEnd: timeOfDaySchema.nullish(),
    isActive: z.boolean().default(true),
})
    .refine((v) => !v.deliveryWindowStart ||
    !v.deliveryWindowEnd ||
    v.deliveryWindowStart < v.deliveryWindowEnd, { message: 'validation.deliveryWindowOrder', path: ['deliveryWindowEnd'] });
/**
 * `.partial()` is unavailable on a refined object, so the update shape is
 * declared from the same field set and re-refined.
 */
export const updateCustomerSchema = z
    .object({
    code: z.string().trim().min(1).max(24).optional(),
    businessName: z.string().trim().min(1).max(200).optional(),
    vatNumber: vatNumberSchema.nullish(),
    fiscalCode: fiscalCodeSchema.nullish(),
    address: z.string().trim().max(300).nullish(),
    provincia: provinciaSchema.nullish(),
    comune: z.string().trim().max(120).nullish(),
    cap: capSchema.nullish(),
    phone: z
        .string()
        .trim()
        .regex(/^[+0-9 ()./-]{6,24}$/, { message: 'validation.phone' })
        .nullish(),
    email: z.string().trim().email({ message: 'validation.email' }).max(160).nullish(),
    contactPerson: z.string().trim().max(120).nullish(),
    notes: z.string().trim().max(2000).nullish(),
    location: latLngSchema.nullish(),
    geofenceRadiusMeters: z.coerce.number().int().min(20).max(2000).optional(),
    deliveryWindowStart: timeOfDaySchema.nullish(),
    deliveryWindowEnd: timeOfDaySchema.nullish(),
    isActive: z.boolean().optional(),
})
    .refine((v) => !v.deliveryWindowStart ||
    !v.deliveryWindowEnd ||
    v.deliveryWindowStart < v.deliveryWindowEnd, { message: 'validation.deliveryWindowOrder', path: ['deliveryWindowEnd'] });
export const customerSchema = z.object({
    id: uuidSchema,
    code: z.string(),
    businessName: z.string(),
    vatNumber: z.string().nullable(),
    fiscalCode: z.string().nullable(),
    address: z.string().nullable(),
    provincia: z.string().nullable(),
    comune: z.string().nullable(),
    cap: z.string().nullable(),
    phone: z.string().nullable(),
    email: z.string().nullable(),
    contactPerson: z.string().nullable(),
    notes: z.string().nullable(),
    location: latLngSchema.nullable(),
    geofenceRadiusMeters: z.number().int(),
    deliveryWindowStart: z.string().nullable(),
    deliveryWindowEnd: z.string().nullable(),
    isActive: z.boolean(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const customerListQueryExtras = z.object({
    provincia: z.string().trim().length(2).optional(),
    isActive: z.enum(['true', 'false']).optional(),
    /** Only customers that have (or lack) a map location. */
    hasLocation: z.enum(['true', 'false']).optional(),
});
//# sourceMappingURL=customer.js.map